// =====================================================
// EDITOR DE PARCELAS
// Gestión de parcelas catastrales de un establecimiento
// src/pages/Agro/Establecimiento/EditorParcelas.js
// =====================================================

import React, { useState, useEffect, useCallback } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Table,
  UncontrolledTooltip,
  InputGroup,
  InputGroupText,
} from "reactstrap"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import toastHandler from "toastHandler"
import { formatearNumero } from "helpers/formatters"

import {
  getParcelasEstablecimiento,
  createParcela,
  updateParcela,
  deleteParcela,
  getParcelasResumen,
  getTiposTenencia,
  getCultivos,
} from "api/agro"

const formatPorcentajeDisplay = value => {
  if (value == null || isNaN(value)) return ""
  const num = Number(value)
  const rounded = Math.round(num * 100) / 100
  const decimals = Math.round((rounded % 1) * 100)
  return decimals === 0
    ? formatearNumero(rounded, 0)
    : formatearNumero(rounded, 2)
}

const FormattedPercentInput = ({ value, onChange, onBlur }) => {
  const [display, setDisplay] = React.useState(
    value != null && !isNaN(value) ? formatPorcentajeDisplay(value) : "",
  )
  const isTyping = React.useRef(false)

  React.useEffect(() => {
    if (!isTyping.current) {
      if (value != null && !isNaN(value)) {
        setDisplay(formatPorcentajeDisplay(value))
      } else {
        setDisplay("")
      }
    }
  }, [value])

  return (
    <InputGroup>
      <input
        type="text"
        inputMode="decimal"
        className="form-control"
        placeholder="0"
        value={display}
        onChange={e => {
          isTyping.current = true
          let raw = e.target.value.replace(/[^\d.]/g, "")
          const dotIndex = raw.indexOf(".")
          if (dotIndex !== -1) {
            const intPart = raw.substring(0, dotIndex)
            const decPart = raw
              .substring(dotIndex + 1)
              .replace(/\./g, "")
              .substring(0, 2)
            raw = intPart + "." + decPart
          }
          setDisplay(raw)
          const num = parseFloat(raw)
          if (!isNaN(num)) {
            onChange(num)
          } else if (raw === "" || raw === ".") {
            onChange(null)
          }
        }}
        onBlur={e => {
          isTyping.current = false
          const num = parseFloat(display)
          if (!isNaN(num)) {
            setDisplay(formatPorcentajeDisplay(num))
          }
          onBlur(e)
        }}
      />
      <InputGroupText>%</InputGroupText>
    </InputGroup>
  )
}

// Esquema de validación
const ParcelaSchema = Yup.object().shape({
  nomenclaturaCatastral: Yup.string().max(100, "Máximo 100 caracteres"),
  superficieTituloHa: Yup.number()
    .min(0, "Debe ser positivo")
    .required("Superficie requerida"),
  codigoTipoTenencia: Yup.string().required("Tipo de tenencia requerido"),
  propietarioRegistral: Yup.string().max(200, "Máximo 200 caracteres"),
  fechaInicioContrato: Yup.date().nullable(),
  fechaFinContrato: Yup.date()
    .nullable()
    .when("fechaInicioContrato", (fechaInicio, schema) => {
      return fechaInicio
        ? schema.min(fechaInicio, "Debe ser posterior a la fecha de inicio")
        : schema
    }),
  canonQqHa: Yup.number().min(0, "Debe ser positivo").nullable(),
  canonUsdHa: Yup.number().min(0, "Debe ser positivo").nullable(),
  valuacionFiscal: Yup.number().min(0, "Debe ser positivo").nullable(),
  porcentajeAparceria: Yup.number()
    .min(0, "Debe ser entre 0 y 100")
    .max(100, "Debe ser entre 0 y 100")
    .nullable(),
})

const EditorParcelas = ({ idEstablecimiento, readonly = false }) => {
  const [loading, setLoading] = useState(true)
  const [parcelas, setParcelas] = useState([])
  const [resumen, setResumen] = useState(null)
  const [tiposTenencia, setTiposTenencia] = useState([])
  const [cultivos, setCultivos] = useState([])

  // Modals
  const [modalEditar, setModalEditar] = useState(false)
  const [modalEliminar, setModalEliminar] = useState(false)
  const [parcelaSeleccionada, setParcelaSeleccionada] = useState(null)
  const [modoEdicion, setModoEdicion] = useState("crear") // crear | editar

  const setSuccess = toastHandler(state => state.setSuccess)
  const setError = toastHandler(state => state.setError)

  // Cargar datos
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [parcelasData, resumenData] = await Promise.all([
        getParcelasEstablecimiento(idEstablecimiento),
        getParcelasResumen(idEstablecimiento),
      ])
      setParcelas(parcelasData)
      setResumen(resumenData)
    } catch (error) {
      setError("Error al cargar parcelas: " + error.message)
    } finally {
      setLoading(false)
    }
  }, [idEstablecimiento])

  const loadCatalogos = async () => {
    try {
      const [tenencias, cultivosData] = await Promise.all([
        getTiposTenencia(),
        getCultivos(),
      ])
      setTiposTenencia(tenencias)
      setCultivos(cultivosData)
    } catch (error) {
      console.error("Error cargando catálogos:", error)
    }
  }

  useEffect(() => {
    loadData()
    loadCatalogos()
  }, [loadData])

  // Abrir modal para crear
  const handleNuevo = () => {
    setParcelaSeleccionada(null)
    setModoEdicion("crear")
    setModalEditar(true)
  }

  // Abrir modal para editar
  const handleEditar = parcela => {
    setParcelaSeleccionada(parcela)
    setModoEdicion("editar")
    setModalEditar(true)
  }

  // Abrir modal de confirmación de eliminación
  const handleConfirmarEliminar = parcela => {
    setParcelaSeleccionada(parcela)
    setModalEliminar(true)
  }

  // Guardar parcela
  const handleGuardar = async (values, { setSubmitting }) => {
    try {
      const data = {
        ...values,
        superficieTituloHa: values.superficieTituloHa
          ? parseFloat(values.superficieTituloHa)
          : null,
        canonQqHa: values.canonQqHa ? parseFloat(values.canonQqHa) : null,
        canonUsdHa: values.canonUsdHa ? parseFloat(values.canonUsdHa) : null,
        valuacionFiscal: values.valuacionFiscal
          ? parseFloat(values.valuacionFiscal)
          : null,
        fechaInicioContrato: values.fechaInicioContrato || null,
        fechaFinContrato: values.fechaFinContrato || null,
        fechaValuacion: values.fechaValuacion || null,
        porcentajeAparceria: values.porcentajeAparceria
          ? parseFloat(values.porcentajeAparceria)
          : null,
      }

      if (modoEdicion === "crear") {
        await createParcela(idEstablecimiento, data)
        setSuccess("Parcela creada correctamente")
      } else {
        await updateParcela(parcelaSeleccionada.id, data)
        setSuccess("Parcela actualizada correctamente")
      }

      setModalEditar(false)
      loadData()
    } catch (error) {
      setError("Error al guardar: " + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  // Eliminar parcela
  const handleEliminar = async () => {
    try {
      await deleteParcela(parcelaSeleccionada.id)
      setSuccess("Parcela eliminada correctamente")
      setModalEliminar(false)
      loadData()
    } catch (error) {
      setError("Error al eliminar: " + error.message)
    }
  }

  // Obtener color del badge según estado del contrato
  const getEstadoContratoBadge = estado => {
    switch (estado) {
      case "VIGENTE":
        return <Badge color="success">Vigente</Badge>
      case "PROXIMO_VENCER":
        return <Badge color="warning">Próximo a vencer</Badge>
      case "VENCIDO":
        return <Badge color="danger">Vencido</Badge>
      default:
        return <Badge color="secondary">N/A</Badge>
    }
  }

  // Valores iniciales del formulario
  const getInitialValues = () => {
    if (parcelaSeleccionada && modoEdicion === "editar") {
      return {
        nomenclaturaCatastral: parcelaSeleccionada.nomenclaturaCatastral || "",
        superficieTituloHa: parcelaSeleccionada.superficieTituloHa || "",
        codigoTipoTenencia: parcelaSeleccionada.codigoTipoTenencia || "",
        propietarioRegistral: parcelaSeleccionada.propietarioRegistral || "",
        fechaInicioContrato: parcelaSeleccionada.fechaInicioContrato || "",
        fechaFinContrato: parcelaSeleccionada.fechaFinContrato || "",
        canonQqHa: parcelaSeleccionada.canonQqHa || "",
        canonUsdHa: parcelaSeleccionada.canonUsdHa || "",
        codigoCultivoReferenciaCannon:
          parcelaSeleccionada.codigoCultivoReferenciaCannon || "",
        valuacionFiscal: parcelaSeleccionada.valuacionFiscal || "",
        monedaValuacion: parcelaSeleccionada.monedaValuacion || "ARS",
        fechaValuacion: parcelaSeleccionada.fechaValuacion || "",
        porcentajeAparceria: parcelaSeleccionada.porcentajeAparceria || "",
      }
    }
    return {
      nomenclaturaCatastral: "",
      superficieTituloHa: "",
      codigoTipoTenencia: "",
      propietarioRegistral: "",
      fechaInicioContrato: "",
      fechaFinContrato: "",
      canonQqHa: "",
      canonUsdHa: "",
      codigoCultivoReferenciaCannon: "",
      valuacionFiscal: "",
      monedaValuacion: "ARS",
      fechaValuacion: "",
      porcentajeAparceria: "",
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner />
        <p className="mt-2 text-muted">Cargando parcelas...</p>
      </div>
    )
  }

  return (
    <>
      {/* Resumen */}
      {resumen && (
        <Row className="mb-3">
          <Col md={2}>
            <Card className="mini-stats-wid">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-2">Total Parcelas</p>
                    <h4 className="mb-0">{resumen.cantidadParcelas}</h4>
                  </div>
                  <div className="avatar-sm align-self-center">
                    <span className="avatar-title rounded-circle bg-primary bg-soft text-primary">
                      <i className="mdi mdi-file-document-multiple font-size-24" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="mini-stats-wid">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-2">
                      Superficie Total
                    </p>
                    <h4 className="mb-0">
                      {resumen.superficieTotal?.toLocaleString("es-AR")} ha
                    </h4>
                  </div>
                  <div className="avatar-sm align-self-center">
                    <span className="avatar-title rounded-circle bg-success bg-soft text-success">
                      <i className="mdi mdi-map font-size-24" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="mini-stats-wid">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-2">Propia</p>
                    <h4 className="mb-0">
                      {resumen.superficiePropia?.toLocaleString("es-AR")} ha
                    </h4>
                  </div>
                  <div className="avatar-sm align-self-center">
                    <span className="avatar-title rounded-circle bg-info bg-soft text-info">
                      <i className="mdi mdi-home font-size-24" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="mini-stats-wid">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-2">Arrendada</p>
                    <h4 className="mb-0">
                      {resumen.superficieArrendada?.toLocaleString("es-AR")} ha
                    </h4>
                  </div>
                  <div className="avatar-sm align-self-center">
                    <span className="avatar-title rounded-circle bg-warning bg-soft text-warning">
                      <i className="mdi mdi-file-sign font-size-24" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="mini-stats-wid">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    {/* BDSJEA-135 */}
                    <p className="text-muted fw-medium mb-2">
                      Contratos por vencer
                    </p>
                    <h4 className="mb-0 text-warning">
                      {resumen.contratosProximosVencer}
                    </h4>
                  </div>
                  <div className="avatar-sm align-self-center">
                    <span className="avatar-title rounded-circle bg-warning bg-soft text-warning">
                      <i className="mdi mdi-clock-alert font-size-24" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="mini-stats-wid">
              <CardBody>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    {/* BDSJEA-135 */}
                    <p className="text-muted fw-medium mb-2">
                      Contratos vencidos
                    </p>
                    <h4 className="mb-0 text-danger">
                      {resumen.contratosVencidos}
                    </h4>
                  </div>
                  <div className="avatar-sm align-self-center">
                    <span className="avatar-title rounded-circle bg-danger bg-soft text-danger">
                      <i className="mdi mdi-alert font-size-24" />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabla de Parcelas */}
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="mdi mdi-file-document-multiple me-2" />
            Parcelas Catastrales
          </h5>
          {!readonly && (
            <Button color="primary" onClick={handleNuevo}>
              <i className="mdi mdi-plus me-1" />
              Nueva Parcela
            </Button>
          )}
        </CardHeader>
        <CardBody>
          {parcelas.length === 0 ? (
            <Alert color="info">
              <i className="mdi mdi-information me-2" />
              No hay parcelas registradas. Agregue la primera parcela catastral.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table className="table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Nomenclatura</th>
                    <th className="text-end">Superficie (ha)</th>
                    <th>Tenencia</th>
                    <th>Propietario</th>
                    <th>Canon</th>
                    <th>Fin Contrato</th>
                    <th>Estado</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {parcelas.map(parcela => (
                    <tr key={parcela.id}>
                      <td>
                        <strong>{parcela.nomenclaturaCatastral || "-"}</strong>
                      </td>
                      <td className="text-end">
                        {parcela.superficieTituloHa?.toLocaleString("es-AR")}
                      </td>
                      <td>
                        <Badge
                          color={
                            parcela.codigoTipoTenencia === "PROPIO"
                              ? "success"
                              : "info"
                          }
                        >
                          {parcela.tipoTenencia}
                        </Badge>
                      </td>
                      <td>{parcela.propietarioRegistral || "-"}</td>
                      <td>
                        {parcela.canonUsdHa ? (
                          <span>USD {parcela.canonUsdHa}/ha</span>
                        ) : parcela.canonQqHa ? (
                          <span>{parcela.canonQqHa} qq/ha</span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {parcela.fechaFinContrato ? (
                          <span>
                            {new Date(
                              parcela.fechaFinContrato,
                            ).toLocaleDateString("es-AR")}
                            {parcela.diasRestantesContrato !== null && (
                              <small className="text-muted ms-1">
                                ({parcela.diasRestantesContrato}d)
                              </small>
                            )}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{getEstadoContratoBadge(parcela.estadoContrato)}</td>
                      <td className="text-center">
                        {!readonly && (
                          <>
                            <Button
                              color="primary"
                              size="sm"
                              className="me-1"
                              id={`edit-${parcela.id}`}
                              onClick={() => handleEditar(parcela)}
                            >
                              <i className="mdi mdi-pencil" />
                            </Button>
                            <UncontrolledTooltip target={`edit-${parcela.id}`}>
                              Editar
                            </UncontrolledTooltip>

                            <Button
                              color="danger"
                              size="sm"
                              id={`delete-${parcela.id}`}
                              onClick={() => handleConfirmarEliminar(parcela)}
                            >
                              <i className="mdi mdi-delete" />
                            </Button>
                            <UncontrolledTooltip
                              target={`delete-${parcela.id}`}
                            >
                              Eliminar
                            </UncontrolledTooltip>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Modal Editar/Crear */}
      <Modal
        isOpen={modalEditar}
        toggle={() => setModalEditar(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setModalEditar(false)}>
          {modoEdicion === "crear" ? "Nueva Parcela" : "Editar Parcela"}
        </ModalHeader>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={ParcelaSchema}
          onSubmit={handleGuardar}
          enableReinitialize
        >
          {({
            errors,
            touched,
            isSubmitting,
            values,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form>
              <ModalBody>
                {/* Datos básicos */}
                <h6 className="text-primary mb-3">
                  <i className="mdi mdi-file-document me-1" />
                  Datos de la Parcela
                </h6>
                <Row>
                  <Col md={8}>
                    <div className="mb-3">
                      <label>Nomenclatura Catastral</label>
                      <Field
                        name="nomenclaturaCatastral"
                        className="form-control"
                        placeholder="Ej: Circ. II - Secc. A - Fracc. 123"
                      />
                      {touched.nomenclaturaCatastral &&
                        errors.nomenclaturaCatastral && (
                          <div className="text-danger small">
                            {errors.nomenclaturaCatastral}
                          </div>
                        )}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label>Superficie (ha) *</label>
                      <Field
                        name="superficieTituloHa"
                        type="number"
                        step="0.01"
                        className="form-control"
                      />
                      {touched.superficieTituloHa &&
                        errors.superficieTituloHa && (
                          <div className="text-danger small">
                            {errors.superficieTituloHa}
                          </div>
                        )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label>Tipo de Tenencia *</label>
                      <Field
                        name="codigoTipoTenencia"
                        as="select"
                        className="form-control"
                      >
                        <option value="">Seleccione...</option>
                        {tiposTenencia.map(t => (
                          <option key={t.codigo} value={t.codigo}>
                            {t.descripcion}
                          </option>
                        ))}
                      </Field>
                      {touched.codigoTipoTenencia &&
                        errors.codigoTipoTenencia && (
                          <div className="text-danger small">
                            {errors.codigoTipoTenencia}
                          </div>
                        )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label>Propietario Registral</label>
                      <Field
                        name="propietarioRegistral"
                        className="form-control"
                      />
                    </div>
                  </Col>
                </Row>

                {/* Porcentaje Aparcería - solo visible cuando Tipo = APARCERIA */}
                {values.codigoTipoTenencia === "APARCERIA" && (
                  <Row>
                    <Col md={4}>
                      <div className="mb-3">
                        <label>Porcentaje Aparcería</label>
                        <FormattedPercentInput
                          value={values.porcentajeAparceria}
                          onChange={val =>
                            setFieldValue("porcentajeAparceria", val)
                          }
                          onBlur={() =>
                            setFieldTouched("porcentajeAparceria", true)
                          }
                        />
                        {touched.porcentajeAparceria &&
                          errors.porcentajeAparceria && (
                            <div className="text-danger small">
                              {errors.porcentajeAparceria}
                            </div>
                          )}
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Contrato de arrendamiento */}
                {values.codigoTipoTenencia === "ARRENDADO" && (
                  <>
                    <hr />
                    <h6 className="text-primary mb-3">
                      <i className="mdi mdi-file-sign me-1" />
                      Contrato de Arrendamiento
                    </h6>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label>Fecha Inicio Contrato</label>
                          <Field
                            name="fechaInicioContrato"
                            type="date"
                            className="form-control"
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label>Fecha Fin Contrato</label>
                          <Field
                            name="fechaFinContrato"
                            type="date"
                            className="form-control"
                          />
                          {touched.fechaFinContrato &&
                            errors.fechaFinContrato && (
                              <div className="text-danger small">
                                {errors.fechaFinContrato}
                              </div>
                            )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <label>Canon (qq/ha)</label>
                          <Field
                            name="canonQqHa"
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder="Ej: 12.5"
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <label>Canon (USD/ha)</label>
                          <Field
                            name="canonUsdHa"
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder="Ej: 350"
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <label>Cultivo Referencia Canon</label>
                          <Field
                            name="codigoCultivoReferenciaCannon"
                            as="select"
                            className="form-control"
                          >
                            <option value="">Seleccione...</option>
                            {cultivos.map(c => (
                              <option key={c.codigo} value={c.codigo}>
                                {c.nombre}
                              </option>
                            ))}
                          </Field>
                        </div>
                      </Col>
                    </Row>
                  </>
                )}

                {/* Valuación Fiscal */}
                <hr />
                <h6 className="text-primary mb-3">
                  <i className="mdi mdi-currency-usd me-1" />
                  Valuación Fiscal
                </h6>
                <Row>
                  <Col md={4}>
                    <div className="mb-3">
                      <label>Valuación Fiscal</label>
                      <Field
                        name="valuacionFiscal"
                        type="number"
                        step="0.01"
                        className="form-control"
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label>Moneda</label>
                      <Field
                        name="monedaValuacion"
                        as="select"
                        className="form-control"
                      >
                        <option value="ARS">ARS</option>
                        <option value="USD">USD</option>
                      </Field>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label>Fecha Valuación</label>
                      <Field
                        name="fechaValuacion"
                        type="date"
                        className="form-control"
                      />
                    </div>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={() => setModalEditar(false)}>
                  Cancelar
                </Button>
                <Button color="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Spinner size="sm" />
                  ) : modoEdicion === "crear" ? (
                    "Crear"
                  ) : (
                    "Guardar"
                  )}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Modal Confirmar Eliminación */}
      <Modal isOpen={modalEliminar} toggle={() => setModalEliminar(false)}>
        <ModalHeader toggle={() => setModalEliminar(false)}>
          Confirmar Eliminación
        </ModalHeader>
        <ModalBody>
          <p>¿Está seguro que desea eliminar la parcela?</p>
          {parcelaSeleccionada && (
            <Alert color="warning">
              <strong>
                {parcelaSeleccionada.nomenclaturaCatastral ||
                  "Sin nomenclatura"}
              </strong>
              <br />
              Superficie:{" "}
              {parcelaSeleccionada.superficieTituloHa?.toLocaleString(
                "es-AR",
              )}{" "}
              ha
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalEliminar(false)}>
            Cancelar
          </Button>
          <Button color="danger" onClick={handleEliminar}>
            <i className="mdi mdi-delete me-1" />
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default EditorParcelas
